import { TimeZone } from '@pocketrn/time-utils';
import { ActionKey, meetingActions } from '../redux/sdk/actions';
import { ManagedProperty, PRNErrorResponse } from '@pocketrn/client/dist/entity-sdk';
import { MeetingSDK } from '../../services/firebase/MeetingSDK';
import { Firestore } from 'firebase/firestore';
import { Meeting, ScheduledMeeting, MultiSelectResponse, ClientCustomCallType, PatientNoShowAction } from '@pocketrn/entities/dist/meeting';
import { Person } from '@pocketrn/entities/dist/community';
import { UserMatchController } from './UserMatch.controller';
import { Provider, User } from '@pocketrn/entities/dist/core';
import { logger } from '@pocketrn/client/dist/app-logger';
import { CursorId } from '@pocketrn/rn-designsystem';

// @NOTE: Redux does not export its Store type.
export type ReduxStore = any;

export class MeetingController {
  public sdk: MeetingSDK;
  public store: ReduxStore;
  public firestore: Firestore;
  public userMatchController: UserMatchController;

  constructor(
    sdk: MeetingSDK,
    store: ReduxStore,
    firestore: Firestore,
    userMatchController: UserMatchController,
  ) {
    this.sdk = sdk;
    this.store = store;
    this.firestore = firestore;
    this.userMatchController = userMatchController;
  }

  public async setMeetingStats(meetingId: string, timeStatsInMinutes: {
    patientLate: number;
    preMeeting: number;
    meeting: number;
    postMeeting: number;
  }, patientNoShow: { action: PatientNoShowAction; context: string | null } | null): Promise<{
    supportTicketId: string | null;
  }> {
    return await this.sdk.setMeetingStats(meetingId, timeStatsInMinutes, patientNoShow);
  }

  public async endMeeting(meetingId: string): Promise<void> {
    await this.sdk.endMeeting(meetingId);
  }

  public async blockMatchedUser(blockUid: string, meetingId: string): Promise<void> {
    await this.sdk.blockUser(blockUid, meetingId);
  }

  public async sendMeetingFeedback(
    meetingId: string,
    feedbackValues: Record<string, number | MultiSelectResponse | null | string>,
    isAcceptor?: boolean,
  ): Promise<void> {
    if (isAcceptor) {
      await this.userMatchController.subscribeToSnapshotMeetings();
    }
    this.store.dispatch(meetingActions.setLoading(ActionKey.Meetings, true));
    await this.sdk.sendMeetingFeedback(meetingId, feedbackValues);
    this.store.dispatch(meetingActions.setLoading(ActionKey.Meetings, false));
  }

  public async clearQueueStats(): Promise<void> {
    await this.userMatchController.getAndSetUserMatch();
  }

  public async getZoomSessionToken(meetingId: string): Promise<string> {
    try {
      return await this.sdk.getZoomSessionToken(meetingId);
    } catch (err) {
      if (err instanceof PRNErrorResponse && err.code === 'SESSION_TOKEN') {
        // @TODO: UI response to error state
        logger.logError('SESSION_TOKEN ERROR:', err);
        throw err;
      } else {
        throw err;
      }
    }
  }

  public async getMeetingsHistory(options?: {
    searchOptions?: { limit?: number, nextPageCursorId?: CursorId },
    managed?: ManagedProperty,
  }) : Promise<{
    meetings: Meeting[];
    personsMap: Record<string, Person>;
    providersMap: Record<string, Provider>;
    customCallTypesMap: Record<string, ClientCustomCallType>;
    usersMap: Record<string, User>;
    nextPageCursorId: CursorId;
  }> {
    return await this.sdk.getMeetingsHistory(options);
  }

  public async getMeeting(meetingId: string, options?: {
    managed?: ManagedProperty,
  }): Promise<{
    meeting: Meeting | undefined;
    scheduledMeeting: ScheduledMeeting | undefined;
    personsMap: Record<string, Person>;
    usersMap: Record<string, User>;
    provider: Provider | undefined;
    customCallType: ClientCustomCallType | undefined;
  }> {
    return await this.sdk.getMeeting(meetingId, options);
  }

  public async updateActiveTimeZone(timeZone: TimeZone): Promise<void> {
    await this.sdk.setActiveTimeZone(timeZone);
  }
}
